import { useEffect, useMemo, useState, type FormEvent, type ReactNode } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Link,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { getQuestions, submitAssessment, type Dimension } from '../api/assessment';
import { getSystemConfig } from '../api/system';

const PROMPT_LIBRARY_URL =
  'https://catalisgov.atlassian.net/wiki/spaces/AS1/pages/26830602256/Prompt+Library';
const AIEVAL_URL = 'https://main.d2j7a3fx6wu0gr.amplifyapp.com/';

function ExternalLink({ href, children }: { href: string; children: ReactNode }) {
  return (
    <Link
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => e.stopPropagation()}
    >
      {children}
    </Link>
  );
}

const PromptLibraryLink = () => (
  <ExternalLink href={PROMPT_LIBRARY_URL}>Prompt Library</ExternalLink>
);

const optionOverrides: Record<string, Record<string, () => ReactNode>> = {
  d2q1: {
    C: () => (
      <>
        I know the <PromptLibraryLink /> exists and use governed prompts sometimes
      </>
    ),
    D: () => (
      <>
        I consistently use governed prompts from the <PromptLibraryLink /> for my work
      </>
    ),
    E: () => (
      <>
        I use governed prompts and have contributed feedback or new prompts to the{' '}
        <PromptLibraryLink />
      </>
    ),
  },
  d5q1: {
    C: () => (
      <>
        I use <ExternalLink href={AIEVAL_URL}>AIEval</ExternalLink> or a similar approach
        occasionally to score prompt output
      </>
    ),
    D: () => (
      <>
        I consistently{' '}
        <ExternalLink href={AIEVAL_URL}>evaluate AI output quality</ExternalLink> and track
        scores over time
      </>
    ),
  },
};

export function Assessment() {
  const navigate = useNavigate();
  const [dimensions, setDimensions] = useState<Dimension[] | null>(null);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [cadencePeriod, setCadencePeriod] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    Promise.all([getQuestions(), getSystemConfig()])
      .then(([qs, cfg]) => {
        setDimensions(qs.dimensions);
        setCadencePeriod(cfg.currentPeriod);
      })
      .catch((err: unknown) => {
        const m = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
        setError(m ?? 'Failed to load assessment');
      });
  }, []);

  const allQuestions = useMemo(
    () => (dimensions ?? []).flatMap((d) => d.questions),
    [dimensions],
  );

  const allAnswered = allQuestions.length > 0 && allQuestions.every((q) => !!answers[q.id]);

  const handleChange = (questionId: string, value: string) => {
    setAnswers((prev) => ({ ...prev, [questionId]: value }));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!allAnswered) {
      setError('Please answer every question before submitting');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const responses = allQuestions.map((q) => ({ questionId: q.id, answer: answers[q.id] }));
      const result = await submitAssessment(cadencePeriod, responses);
      navigate('/dashboard', {
        replace: true,
        state: { lastAssessmentId: result.assessmentId, lastTotal: result.totalScore, lastLevel: result.maturityLevel },
      });
    } catch (err: unknown) {
      const m = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      setError(m ?? 'Submission failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (!dimensions) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        {error ? <Alert severity="error">{error}</Alert> : <CircularProgress />}
      </Box>
    );
  }

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box>
        <Typography variant="h4" component="h1">
          AI Maturity Self-Assessment
        </Typography>
        <Typography variant="body2" sx={{ mt: 1 }}>
          Cadence period: <strong>{cadencePeriod || '—'}</strong>
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" role="alert">
          {error}
        </Alert>
      )}

      {dimensions.map((dim) => (
        <Card elevation={3} key={dim.id}>
          <CardContent>
            <Typography variant="h5" component="h2">
              {dim.name}
            </Typography>
            <Typography variant="caption">Weight: {(dim.weight * 100).toFixed(0)}%</Typography>
            {dim.questions.map((q) => (
              <Box key={q.id} sx={{ mt: 3 }}>
                <Typography variant="subtitle1" component="div" sx={{ fontWeight: 500 }}>
                  {q.text}
                </Typography>
                <FormControl component="fieldset" sx={{ mt: 1 }}>
                  <RadioGroup
                    value={answers[q.id] ?? ''}
                    onChange={(_, v) => handleChange(q.id, v)}
                  >
                    {q.options.map((opt) => {
                      const override = optionOverrides[q.id]?.[opt.letter];
                      const labelContent: ReactNode = override ? override() : opt.text;
                      return (
                        <FormControlLabel
                          key={opt.letter}
                          value={opt.letter}
                          control={<Radio />}
                          label={
                            <>
                              {opt.letter}) {labelContent}
                            </>
                          }
                        />
                      );
                    })}
                  </RadioGroup>
                </FormControl>
              </Box>
            ))}
          </CardContent>
        </Card>
      ))}

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
        <Button onClick={() => navigate('/dashboard')} variant="text" color="primary">
          CANCEL
        </Button>
        <Button type="submit" variant="contained" color="primary" disabled={!allAnswered || submitting}>
          SUBMIT
        </Button>
      </Box>
    </Box>
  );
}
